import React, { Suspense, lazy, useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";

import RoutingPaths from "../utils/RoutingPaths";

import DashboardLayout from "../layout/dashboard/DashboardLayout";
import SectionLoader from "../components/global/loader/SectionLoader";
import { removeDashboardPath } from "../utils/HelperFunctions";
import InvoiceTab from "../pages/org_dashboard/payments/tabs/InvoiceTab";
import PaymentTab from "../pages/org_dashboard/payments/tabs/PaymentTab";
import PageNotLoaded from "../pages/org_dashboard/other/PageNotLoaded";
import { ROLE_ASSISTANT, ROLE_FRONT_DESK_ADMIN } from "../utils/Constants";

const Login = lazy(() => import("../pages/auth/Login"));
const Dashboard = lazy(
  () => import("../pages/org_dashboard/dashboard/Dashboard"),
);
const Appointments = lazy(
  () => import("../pages/org_dashboard/appointments/Appointments"),
);
const Consultants = lazy(
  () => import("../pages/org_dashboard/consultants/Consultants"),
);
const AddConsultants = lazy(
  () => import("../pages/org_dashboard/consultants/AddConsultants"),
);
const ConsultantDetails = lazy(
  () => import("../pages/org_dashboard/consultants/ConsultantDetails"),
);
const NewAppointment = lazy(
  () => import("../pages/org_dashboard/appointments/NewAppointment"),
);
const NewAppointmentWithoutClient = lazy(
  () =>
    import("../pages/org_dashboard/appointments/NewAppointmentWithoutClient"),
);
const AppointmentDetails = lazy(
  () => import("../pages/org_dashboard/appointments/AppointmentDetails"),
);
const AccountSettings = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/AccountSettings"),
);
const AddressAndTiming = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/address/AddressAndTiming"),
);
const AddAddressAndTiming = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/address/AddAddressAndTiming"),
);

const Holidays = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/address/Holidays.js"),
);

const Admins = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/admins/AdminUsers"),
);

const ReportsLayout = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/reports_layouts/ReportsLayout"),
);

const Exercise = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/exercise/Exercise"),
);

const ExerciseTemplate = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/exerciseTemplates/ExerciseTemplate"),
);

const Referrer = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/referrer/Referrer"),
);

const Contacts = lazy(() => import("../pages/org_dashboard/contacts/Contacts"));
const ContactDetails = lazy(
  () => import("../pages/org_dashboard/contacts/ContactDetails"),
);
const EditContact = lazy(
  () => import("../pages/org_dashboard/contacts/EditContact"),
);
const Schedule = lazy(() => import("../pages/org_dashboard/schedule/Schedule"));
// const Messages = lazy(() => import("../pages/org_dashboard/messages/Messages"));
const Payments = lazy(() => import("../pages/org_dashboard/payments/Payments"));

const EditGeneralInformation = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/general_information/EditGeneralInformation"),
);

const EditBirthdayEmailSetting = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/birthday_email_setting/BirthdayEmailSetting"),
);

const BillCodes = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/bill_codes/BillCodes"),
);

const ReferrerDetails = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/referrer/ReferrerDetails"),
);

const ServicePackages = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/service_packages/Packages"),
);

const Treatment = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/treatment/Treatment"),
);

const CalendarConsent = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/add_appointment_calender/CalendarConsent"),
);

const Reports = lazy(() => import("../pages/org_dashboard/reports/Reports"));

const AddEditClientConsentForm = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/client_consent_form/AddEditClientConsentForm"),
);

const ClientConsentForms = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/client_consent_form/ClientConsentForms"),
);

const CasePaperDesigns = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/case_paper/CasePaperDesigns"),
);

const ClientAdditionalInformation = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/client_additional_info/ClientAdditionalInfo"),
);

const Vaccines = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/vaccines/Vaccines"),
);

const MedicineInformation = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/medicine_information/MedicineInfo.js"),
);

const CertificateTemplate = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/certificateTemplate/CertificateTemplate.js"),
);

const AddEditCertificateTemplate = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/certificateTemplate/AddEditCertificateTemplate.js"),
);

const HospitalizationDetails = lazy(
  () => import("../pages/org_dashboard/hospitalization/HospitalizationDetails"),
);

const Hospitalization = lazy(
  () => import("../pages/org_dashboard/hospitalization/Hospitalization"),
);

const Inventories = lazy(
  () => import("../pages/org_dashboard/inventory/Inventories.js"),
);

const MedicalProductDetails = lazy(
  () => import("../pages/org_dashboard/inventory/MedicalProductDetails"),
);

const VitalSigns = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings//vitals/VitalSigns.js"),
);

const EducationalMaterial = lazy(
  () =>
    import("../pages/org_dashboard/profile/account_settings/educational_material/EducationalMaterial.js"),
);

export default function OrgDashboardRouter() {
  const [organizationDto, setOrganizationDto] = useState({});
  const client = sessionStorage.getItem("ClientDetails");
  const isClientUser = client !== null;

  const consultant = JSON.parse(sessionStorage.getItem("ConsultantDetails"));
  const isConsultantUser = consultant !== null;

  const userRole = JSON.parse(sessionStorage.getItem("UserRole"));

  const orgDetails = JSON.parse(sessionStorage.getItem("OrgDetails"));
  const isReportAccessAllowed =
    userRole === ROLE_ASSISTANT || userRole === ROLE_FRONT_DESK_ADMIN
      ? orgDetails && orgDetails?.isReportAccessAllowed
        ? orgDetails?.isReportAccessAllowed
        : false
      : true;

  const googleClientId = JSON.parse(sessionStorage.getItem("googleClientId"));
  const googleRedirectUri = JSON.parse(
    sessionStorage.getItem("googleRedirectUri"),
  );

  useEffect(() => {
    const handleOrgUpdate = () => {
      const updatedOrg = JSON.parse(sessionStorage.getItem("OrgDetails2"));
      setOrganizationDto(updatedOrg);
    };

    window.addEventListener("orgDetailsUpdated", handleOrgUpdate);

    return () => {
      window.removeEventListener("orgDetailsUpdated", handleOrgUpdate);
    };
  }, []);

  useEffect(() => {
    setOrganizationDto(JSON.parse(sessionStorage.getItem("OrgDetails2")));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Suspense fallback={<SectionLoader />}>
      <Routes>
        <Route exact element={<DashboardLayout />} errorElement={<Login />}>
          {!isClientUser &&
            !isConsultantUser &&
            userRole !== ROLE_FRONT_DESK_ADMIN &&
            userRole !== ROLE_ASSISTANT && (
              <>
                <Route exact index element={<Dashboard />} />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.APPOINTMENTS)}
                  element={<Appointments />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CONSULTANTS)}
                  element={<Consultants />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ADD_CONSULTANTS)}
                  element={<AddConsultants />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.EDIT_CONSULTANTS)}
                  element={<AddConsultants />}
                />
                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.CONSULTANT_DETAILS + "/:id",
                  )}
                  element={<ConsultantDetails />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CONSULTANT_HOLIDAYS)}
                >
                  <Route index element={<Holidays />} />
                </Route>
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.NEW_APPOINTMENT)}
                  element={<NewAppointment />}
                />
                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.NEW_APPOINTMENT_WITHOUT_CLIENT,
                  )}
                  element={<NewAppointmentWithoutClient />}
                />
                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.APPOINTMENT_DETAILS + "/:id",
                  )}
                  element={<AppointmentDetails />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CONTACTS)}
                  element={<Contacts />}
                />

                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.CONTACT_DETAILS + "/:id",
                  )}
                  element={<ContactDetails />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CONTACT_UPDATE)}
                  element={<EditContact />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CONTACT_ADD)}
                  element={<EditContact />}
                />

                {/* <Route
                exact
                path={removeDashboardPath(RoutingPaths.MESSAGES)}
                element={<Messages />}
              /> */}

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.SCHEDULE)}
                  element={<Schedule />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.PAYMENTS)}
                  element={<Payments />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CREATE_INVOICE)}
                  element={<InvoiceTab />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.PAYMENTS_TAB)}
                  element={<PaymentTab />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ACCOUNT_SETTINGS)}
                  element={<AccountSettings />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ADDRESS_AND_TIMINGS)}
                >
                  <Route index element={<AddressAndTiming />} />

                  <Route
                    exact
                    path={removeDashboardPath(
                      RoutingPaths.ADD_ADDRESS_AND_TIMINGS,
                    )}
                    element={<AddAddressAndTiming />}
                  />
                  <Route
                    exact
                    path={removeDashboardPath(
                      RoutingPaths.EDIT_ADDRESS_AND_TIMINGS,
                    )}
                    element={<AddAddressAndTiming />}
                  />

                  <Route
                    exact
                    path={removeDashboardPath(RoutingPaths.HOLIDAYS + "/:id")}
                  >
                    <Route index element={<Holidays />} />
                  </Route>
                </Route>

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.GENERAL_INFORMATION)}
                  element={<EditGeneralInformation />}
                />

                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.BIRTHDAY_EMAIL_SETTING,
                  )}
                  element={<EditBirthdayEmailSetting />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.BillCodes)}
                  element={<BillCodes />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ADMINS)}
                  element={<Admins />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ReportsLayout)}
                  element={<ReportsLayout />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.Exercise)}
                  element={<Exercise />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.Exercise) + "/:id"}
                  element={<Exercise />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ExerciseTemplate)}
                  element={<ExerciseTemplate />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ADD_EDIT_VACCINES)}
                  element={<Vaccines />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.REFERRERS)}
                  element={<Referrer />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.REFERRERS) + "/:id"}
                  element={<ReferrerDetails />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.PACKAGES)}
                  element={<ServicePackages />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.TREATMENTS)}
                  element={<Treatment />}
                />

                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.BIRTHDAY_EMAIL_SETTING,
                  )}
                  element={<EditBirthdayEmailSetting />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.REPORTS)}
                  element={<Reports />}
                />

                {organizationDto?.isMedicalInventory && (
                  <>
                    <Route
                      exact
                      path={removeDashboardPath(RoutingPaths.INVENTORIES)}
                      element={<Inventories />}
                    />

                    <Route
                      exact
                      path={
                        removeDashboardPath(
                          RoutingPaths.MEDICAL_PRODUCT_DETAILS,
                        ) + "/:id"
                      }
                      element={<MedicalProductDetails />}
                    />
                  </>
                )}

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CONSENT_FORMS)}
                  element={<ClientConsentForms />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ADD_CONSENT_FORMS)}
                  element={<AddEditClientConsentForm />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.EDIT_CONSENT_FORMS)}
                  element={<AddEditClientConsentForm />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CASE_PAPER_DESIGNS)}
                  element={<CasePaperDesigns />}
                />

                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.CLIENT_ADDITIONAL_INFO,
                  )}
                  element={<ClientAdditionalInformation />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.MEDICINE_INFORMATION)}
                  element={<MedicineInformation />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CERTIFICATE_TEMPLATES)}
                  element={<CertificateTemplate />}
                />
                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.ADD_CERTIFICATE_TEMPLATE,
                  )}
                  element={<AddEditCertificateTemplate />}
                />

                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.EDIT_CERTIFICATE_TEMPLATE,
                  )}
                  element={<AddEditCertificateTemplate />}
                />

                {organizationDto?.isUseHospitalization && (
                  <>
                    <Route
                      exact
                      path={removeDashboardPath(
                        RoutingPaths.HOSPITALIZATION_DETAILS + "/:id",
                      )}
                      element={<HospitalizationDetails />}
                    />

                    <Route
                      exact
                      path={removeDashboardPath(RoutingPaths.HOSPITALIZATION)}
                      element={<Hospitalization />}
                    />
                  </>
                )}
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.VITALS)}
                  element={<VitalSigns />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.EDUCATIONAL_MATERIAL)}
                  element={<EducationalMaterial />}
                />

                <Route exact path="*" element={<PageNotLoaded />} />
              </>
            )}
          {!isClientUser &&
            !isConsultantUser &&
            userRole === ROLE_FRONT_DESK_ADMIN && (
              <>
                <Route exact index element={<Dashboard />} />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.APPOINTMENTS)}
                  element={<Appointments />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CONSULTANTS)}
                  element={<Consultants />}
                />
                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.CONSULTANT_DETAILS + "/:id",
                  )}
                  element={<ConsultantDetails />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CONSULTANT_HOLIDAYS)}
                >
                  <Route index element={<Holidays />} />
                </Route>
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.NEW_APPOINTMENT)}
                  element={<NewAppointment />}
                />
                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.NEW_APPOINTMENT_WITHOUT_CLIENT,
                  )}
                  element={<NewAppointmentWithoutClient />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CONTACTS)}
                  element={<Contacts />}
                />

                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.CONTACT_DETAILS + "/:id",
                  )}
                  element={<ContactDetails />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CONTACT_UPDATE)}
                  element={<EditContact />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CONTACT_ADD)}
                  element={<EditContact />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.SCHEDULE)}
                  element={<Schedule />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.PAYMENTS)}
                  element={<Payments />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CREATE_INVOICE)}
                  element={<InvoiceTab />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.PAYMENTS_TAB)}
                  element={<PaymentTab />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ACCOUNT_SETTINGS)}
                  element={<AccountSettings />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ADDRESS_AND_TIMINGS)}
                >
                  <Route index element={<AddressAndTiming />} />
                </Route>

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.HOLIDAYS + "/:id")}
                >
                  <Route index element={<Holidays />} />
                </Route>

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.BillCodes)}
                  element={<BillCodes />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ADMINS)}
                  element={<Admins />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ReportsLayout)}
                  element={<ReportsLayout />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.Exercise)}
                  element={<Exercise />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.Exercise) + "/:id"}
                  element={<Exercise />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ExerciseTemplate)}
                  element={<ExerciseTemplate />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.REFERRERS)}
                  element={<Referrer />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.REFERRERS) + "/:id"}
                  element={<ReferrerDetails />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.PACKAGES)}
                  element={<ServicePackages />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.TREATMENTS)}
                  element={<Treatment />}
                />

                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.BIRTHDAY_EMAIL_SETTING,
                  )}
                  element={<EditBirthdayEmailSetting />}
                />

                {isReportAccessAllowed && (
                  <Route
                    exact
                    path={removeDashboardPath(RoutingPaths.REPORTS)}
                    element={<Reports />}
                  />
                )}

                {orgDetails?.isAppointmentDetailsAccessAllowed && (
                  <Route
                    exact
                    path={removeDashboardPath(
                      RoutingPaths.APPOINTMENT_DETAILS + "/:id",
                    )}
                    element={<AppointmentDetails />}
                  />
                )}

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ADD_EDIT_VACCINES)}
                  element={<Vaccines />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.MEDICINE_INFORMATION)}
                  element={<MedicineInformation />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CERTIFICATE_TEMPLATES)}
                  element={<CertificateTemplate />}
                />
                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.ADD_CERTIFICATE_TEMPLATE,
                  )}
                  element={<AddEditCertificateTemplate />}
                />

                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.EDIT_CERTIFICATE_TEMPLATE,
                  )}
                  element={<AddEditCertificateTemplate />}
                />

                {organizationDto?.isUseHospitalization && (
                  <>
                    <Route
                      exact
                      path={removeDashboardPath(
                        RoutingPaths.HOSPITALIZATION_DETAILS + "/:id",
                      )}
                      element={<HospitalizationDetails />}
                    />

                    <Route
                      exact
                      path={removeDashboardPath(RoutingPaths.HOSPITALIZATION)}
                      element={<Hospitalization />}
                    />
                  </>
                )}

                {organizationDto.isMedicalInventory && (
                  <>
                    <Route
                      exact
                      path={removeDashboardPath(RoutingPaths.INVENTORIES)}
                      element={<Inventories />}
                    />

                    <Route
                      exact
                      path={
                        removeDashboardPath(
                          RoutingPaths.MEDICAL_PRODUCT_DETAILS,
                        ) + "/:id"
                      }
                      element={<MedicalProductDetails />}
                    />
                  </>
                )}

                {orgDetails?.isAppointmentDetailsAccessAllowed && (
                  <Route
                    exact
                    path={removeDashboardPath(RoutingPaths.VITALS)}
                    element={<VitalSigns />}
                  />
                )}
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.EDUCATIONAL_MATERIAL)}
                  element={<EducationalMaterial />}
                />
                <Route exact path="*" element={<PageNotLoaded />} />
              </>
            )}

          {!isClientUser &&
            !isConsultantUser &&
            userRole === ROLE_ASSISTANT && (
              <>
                <Route exact index element={<Dashboard />} />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ADDRESS_AND_TIMINGS)}
                >
                  <Route index element={<AddressAndTiming />} />

                  <Route
                    exact
                    path={removeDashboardPath(RoutingPaths.HOLIDAYS + "/:id")}
                  >
                    <Route index element={<Holidays />} />
                  </Route>
                </Route>
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.APPOINTMENTS)}
                  element={<Appointments />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CONSULTANTS)}
                  element={<Consultants />}
                />
                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.CONSULTANT_DETAILS + "/:id",
                  )}
                  element={<ConsultantDetails />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CONSULTANT_HOLIDAYS)}
                >
                  <Route index element={<Holidays />} />
                </Route>
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.NEW_APPOINTMENT)}
                  element={<NewAppointment />}
                />
                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.NEW_APPOINTMENT_WITHOUT_CLIENT,
                  )}
                  element={<NewAppointmentWithoutClient />}
                />
                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.APPOINTMENT_DETAILS + "/:id",
                  )}
                  element={<AppointmentDetails />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CONTACTS)}
                  element={<Contacts />}
                />

                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.CONTACT_DETAILS + "/:id",
                  )}
                  element={<ContactDetails />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CONTACT_UPDATE)}
                  element={<EditContact />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CONTACT_ADD)}
                  element={<EditContact />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.SCHEDULE)}
                  element={<Schedule />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.BillCodes)}
                  element={<BillCodes />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.PACKAGES)}
                  element={<ServicePackages />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ACCOUNT_SETTINGS)}
                  element={<AccountSettings />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ADDRESS_AND_TIMINGS)}
                ></Route>

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.Exercise)}
                  element={<Exercise />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.Exercise) + "/:id"}
                  element={<Exercise />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ExerciseTemplate)}
                  element={<ExerciseTemplate />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.REFERRERS)}
                  element={<Referrer />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.REFERRERS) + "/:id"}
                  element={<ReferrerDetails />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ADMINS)}
                  element={<Admins />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ReportsLayout)}
                  element={<ReportsLayout />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.TREATMENTS)}
                  element={<Treatment />}
                />

                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.BIRTHDAY_EMAIL_SETTING,
                  )}
                  element={<EditBirthdayEmailSetting />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.PAYMENTS)}
                  element={<Payments />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CREATE_INVOICE)}
                  element={<InvoiceTab />}
                />
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.PAYMENTS_TAB)}
                  element={<PaymentTab />}
                />

                {isReportAccessAllowed && (
                  <Route
                    exact
                    path={removeDashboardPath(RoutingPaths.REPORTS)}
                    element={<Reports />}
                  />
                )}

                {orgDetails?.isAppointmentDetailsAccessAllowed && (
                  <Route
                    exact
                    path={removeDashboardPath(
                      RoutingPaths.APPOINTMENT_DETAILS + "/:id",
                    )}
                    element={<AppointmentDetails />}
                  />
                )}

                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.APPOINTMENT_DETAILS + "/:id",
                  )}
                  element={<AppointmentDetails />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ADD_EDIT_VACCINES)}
                  element={<Vaccines />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.MEDICINE_INFORMATION)}
                  element={<MedicineInformation />}
                />

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CERTIFICATE_TEMPLATES)}
                  element={<CertificateTemplate />}
                />
                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.ADD_CERTIFICATE_TEMPLATE,
                  )}
                  element={<AddEditCertificateTemplate />}
                />

                <Route
                  exact
                  path={removeDashboardPath(
                    RoutingPaths.EDIT_CERTIFICATE_TEMPLATE,
                  )}
                  element={<AddEditCertificateTemplate />}
                />

                {organizationDto?.isUseHospitalization && (
                  <>
                    <Route
                      exact
                      path={removeDashboardPath(
                        RoutingPaths.HOSPITALIZATION_DETAILS + "/:id",
                      )}
                      element={<HospitalizationDetails />}
                    />

                    <Route
                      exact
                      path={removeDashboardPath(RoutingPaths.HOSPITALIZATION)}
                      element={<Hospitalization />}
                    />
                  </>
                )}

                {organizationDto.isMedicalInventory && (
                  <>
                    <Route
                      exact
                      path={removeDashboardPath(RoutingPaths.INVENTORIES)}
                      element={<Inventories />}
                    />

                    <Route
                      exact
                      path={
                        removeDashboardPath(
                          RoutingPaths.MEDICAL_PRODUCT_DETAILS,
                        ) + "/:id"
                      }
                      element={<MedicalProductDetails />}
                    />
                  </>
                )}

                {orgDetails?.isAppointmentDetailsAccessAllowed && (
                  <Route
                    exact
                    path={removeDashboardPath(RoutingPaths.VITALS)}
                    element={<VitalSigns />}
                  />
                )}

                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.EDUCATIONAL_MATERIAL)}
                  element={<EducationalMaterial />}
                />

                <Route exact path="*" element={<PageNotLoaded />} />
              </>
            )}
          {isClientUser && (
            <>
              <Route
                exact
                path={removeDashboardPath(RoutingPaths.NEW_APPOINTMENT)}
                element={<NewAppointment />}
              />

              <Route
                exact
                path={removeDashboardPath(RoutingPaths.CONTACT_DETAIL)}
                element={<ContactDetails />}
              />
              <Route
                exact
                path={removeDashboardPath(RoutingPaths.EDIT_DETAILS)}
                element={<EditContact />}
              />
              <Route exact path="*" element={<PageNotLoaded />} />
            </>
          )}
          {isConsultantUser && (
            <>
              <Route exact index element={<Dashboard />} />
              <Route
                exact
                path={removeDashboardPath(RoutingPaths.APPOINTMENTS)}
                element={<Appointments />}
              />
              <Route
                exact
                path={removeDashboardPath(RoutingPaths.EDIT_PERSONAL_DETAILS)}
                element={<AddConsultants />}
              />
              <Route
                exact
                path={removeDashboardPath(RoutingPaths.CONSULTANT_DETAIL)}
                element={<ConsultantDetails />}
              />

              <Route
                exact
                path={removeDashboardPath(RoutingPaths.CONSULTANT_HOLIDAYS)}
              >
                <Route index element={<Holidays />} />
              </Route>
              <Route
                exact
                path={removeDashboardPath(RoutingPaths.NEW_APPOINTMENT)}
                element={<NewAppointment />}
              />
              <Route
                exact
                path={removeDashboardPath(
                  RoutingPaths.NEW_APPOINTMENT_WITHOUT_CLIENT,
                )}
                element={<NewAppointmentWithoutClient />}
              />
              <Route
                exact
                path={removeDashboardPath(
                  RoutingPaths.APPOINTMENT_DETAILS + "/:id",
                )}
                element={<AppointmentDetails />}
              />

              <Route
                exact
                path={removeDashboardPath(RoutingPaths.CONTACTS)}
                element={<Contacts />}
              />

              <Route
                exact
                path={removeDashboardPath(
                  RoutingPaths.CONTACT_DETAILS + "/:id",
                )}
                element={<ContactDetails />}
              />

              <Route
                exact
                path={removeDashboardPath(RoutingPaths.CONTACT_UPDATE)}
                element={<EditContact />}
              />

              <Route
                exact
                path={removeDashboardPath(RoutingPaths.CONTACT_ADD)}
                element={<EditContact />}
              />

              {/* <Route
                exact
                path={removeDashboardPath(RoutingPaths.MESSAGES)}
                element={<Messages />}
              /> */}

              <Route
                exact
                path={removeDashboardPath(RoutingPaths.SCHEDULE)}
                element={<Schedule />}
              />

              {consultant?.useHospitalization && (
                <>
                  <Route
                    exact
                    path={removeDashboardPath(
                      RoutingPaths.HOSPITALIZATION_DETAILS + "/:id",
                    )}
                    element={<HospitalizationDetails />}
                  />

                  <Route
                    exact
                    path={removeDashboardPath(RoutingPaths.HOSPITALIZATION)}
                    element={<Hospitalization />}
                  />
                </>
              )}
              {(consultant.isPaymentInfoAccessAllowed === true ||
                consultant.isPaymentInfoAccessAllowed === null) && (
                <>
                  <Route
                    exact
                    path={removeDashboardPath(RoutingPaths.PAYMENTS)}
                    element={<Payments />}
                  />

                  <Route
                    exact
                    path={removeDashboardPath(RoutingPaths.CREATE_INVOICE)}
                    element={<InvoiceTab />}
                  />
                  <Route
                    exact
                    path={removeDashboardPath(RoutingPaths.PAYMENTS_TAB)}
                    element={<PaymentTab />}
                  />
                  <Route
                    exact
                    path={removeDashboardPath(RoutingPaths.BillCodes)}
                    element={<BillCodes />}
                  />

                  <Route
                    exact
                    path={removeDashboardPath(RoutingPaths.PACKAGES)}
                    element={<ServicePackages />}
                  />

                  <Route
                    exact
                    path={removeDashboardPath(RoutingPaths.REPORTS)}
                    element={<Reports />}
                  />
                </>
              )}
              <Route
                exact
                path={removeDashboardPath(RoutingPaths.ACCOUNT_SETTINGS)}
                element={<AccountSettings />}
              />

              <Route
                exact
                path={removeDashboardPath(RoutingPaths.ADDRESS_AND_TIMINGS)}
              >
                <Route index element={<AddressAndTiming />} />
              </Route>

              <Route
                exact
                path={removeDashboardPath(RoutingPaths.HOLIDAYS + "/:id")}
              >
                <Route index element={<Holidays />} />
              </Route>

              {(consultant.showExercise === true ||
                consultant.showExercise === null) && (
                <>
                  <Route
                    exact
                    path={removeDashboardPath(RoutingPaths.Exercise)}
                    element={<Exercise />}
                  />
                  <Route
                    exact
                    path={removeDashboardPath(RoutingPaths.Exercise) + "/:id"}
                    element={<Exercise />}
                  />

                  <Route
                    exact
                    path={removeDashboardPath(RoutingPaths.ExerciseTemplate)}
                    element={<ExerciseTemplate />}
                  />
                </>
              )}

              <Route
                exact
                path={removeDashboardPath(RoutingPaths.REFERRERS)}
                element={<Referrer />}
              />
              <Route
                exact
                path={removeDashboardPath(RoutingPaths.REFERRERS) + "/:id"}
                element={<ReferrerDetails />}
              />
              {googleClientId && googleRedirectUri && (
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.CALENDAR_CONSENT)}
                  element={<CalendarConsent />}
                />
              )}

              {consultant?.showImmunizationSchedule && (
                <Route
                  exact
                  path={removeDashboardPath(RoutingPaths.ADD_EDIT_VACCINES)}
                  element={<Vaccines />}
                />
              )}

              <Route
                exact
                path={removeDashboardPath(RoutingPaths.MEDICINE_INFORMATION)}
                element={<MedicineInformation />}
              />

              <Route
                exact
                path={removeDashboardPath(RoutingPaths.CERTIFICATE_TEMPLATES)}
                element={<CertificateTemplate />}
              />
              <Route
                exact
                path={removeDashboardPath(
                  RoutingPaths.ADD_CERTIFICATE_TEMPLATE,
                )}
                element={<AddEditCertificateTemplate />}
              />

              <Route
                exact
                path={removeDashboardPath(
                  RoutingPaths.EDIT_CERTIFICATE_TEMPLATE,
                )}
                element={<AddEditCertificateTemplate />}
              />

              <Route
                exact
                path={removeDashboardPath(RoutingPaths.VITALS)}
                element={<VitalSigns />}
              />

              <Route
                exact
                path={removeDashboardPath(RoutingPaths.EDUCATIONAL_MATERIAL)}
                element={<EducationalMaterial />}
              />

              <Route exact path="*" element={<PageNotLoaded />} />
            </>
          )}
        </Route>
      </Routes>
    </Suspense>
  );
}
